import {
  WSCard,
  WSElement,
  WSField,
  WSFileInput,
  WSFlexBox,
  WSForm,
  WSInputText,
  WSList,
  WSSectionToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import { ChangeEvent, useCallback, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { FormDataAttachment } from ".";
import { useMutationUploadVaultFile } from "../../../../v3/utils/useMutationUploadVaultFile";
import { WorkLogAttachmentsTable } from "../../../WorkLogs/components/WorkLogAttachmentsTable";

type Props = {
  notesInitialVisible?: boolean;
};

export const AdditionalDetails: React.FC<Props> = props => {
  const { register, control, trigger, setValue } = useFormContext();

  const [uploadVaultFile, uploadVaultFileMeta] = useMutationUploadVaultFile();
  const {
    fields: attachmentsField,
    append,
    remove,
    insert
  } = useFieldArray<FormDataAttachment>({
    control,
    name: "attachments"
  });

  const [notesVisible, setNotesVisible] = useState(
    props.notesInitialVisible || false
  );
  const visibleAttachments = attachmentsField.filter(
    attachment => attachment.fileId && !attachment.remove
  );

  const openFileUpload = useCallback(() => {
    document.getElementById("attachmentsFileUpload")?.click();
  }, []);

  return (
    <WSElement>
      <WSSectionToolbar title="Additional details (optional)" />

      <WSList gap="XL">
        <WSForm.Field
          hidden={!notesVisible}
          name="notes"
          label="Notes (optional)"
          component={WSInputText}
          componentProps={{
            action: {
              text: "Remove",
              onClick: () => {
                setValue("notes", "");
                setNotesVisible(false);
              }
            }
          }}
        />

        {attachmentsField.map((attachment, index: number) =>
          attachment.fileId ? (
            <>
              <input
                ref={register()}
                type="hidden"
                name={`attachments[${index}].remove`}
                defaultValue={attachment.remove}
              />
              <input
                ref={register()}
                type="hidden"
                name={`attachments[${index}].fileId`}
                defaultValue={attachment.fileId}
              />
            </>
          ) : null
        )}

        <WSElement hidden>
          <WSFileInput
            id="attachmentsFileUpload"
            name="attachmentsFileUpload"
            onChange={async (event: ChangeEvent<HTMLInputElement>) => {
              event.stopPropagation();
              event.preventDefault();
              if (event.target && event.target.files && event.target.files) {
                const file = event.target.files[0];
                await uploadVaultFile(
                  { file },
                  {
                    onSuccess: async vaultFile => {
                      append({ fileId: vaultFile.id! });
                      // manually trigger validation for attachments
                      await trigger("attachments");
                    }
                  }
                );
              }
            }}
          ></WSFileInput>
        </WSElement>

        <WSElement>
          {visibleAttachments.length > 0 && (
            <>
              <WSField
                label="Attachments (optional)"
                action={{
                  text: "Add attachment",
                  onClick: () => {
                    openFileUpload();
                  }
                }}
              />

              <WorkLogAttachmentsTable
                fileIds={attachmentsField
                  .filter(attachment => attachment.fileId && !attachment.remove)
                  .map(attachment => attachment.fileId as string)}
                onRemove={fileId => {
                  const index = attachmentsField.findIndex(
                    attachment => attachment.fileId === fileId
                  );
                  remove(index);
                  insert(index, { fileId, remove: "1" });
                  // manually trigger validation for attachments
                  trigger("attachments");
                }}
              />
            </>
          )}

          {uploadVaultFileMeta.isError && (
            <WSText.ParagraphSm color="garnet" my="M">
              Ooops! Something wrong with this file..
            </WSText.ParagraphSm>
          )}
        </WSElement>

        {(!notesVisible || visibleAttachments.length === 0) && (
          <WSFlexBox gap="L">
            {!notesVisible && (
              <WSCard
                onClick={() => {
                  setNotesVisible(true);
                }}
                header={{
                  value: {
                    action: {
                      icon: "note",
                      label: "Add notes"
                    }
                  }
                }}
              />
            )}

            {visibleAttachments.length === 0 && (
              <WSCard
                onClick={() => {
                  openFileUpload();
                }}
                header={{
                  value: {
                    action: {
                      icon: "paperclip",
                      label: "Add attachment"
                    }
                  }
                }}
              />
            )}
          </WSFlexBox>
        )}
      </WSList>
    </WSElement>
  );
};
